import { useEffect, useMemo, useState } from "react";
import { logConnectorFailure } from "../../../telemetry";
import type { AttachmentInfo, CalEvent, WorkingHours } from "../types";
import { focusChange, moveModeFor, proposeChange, rescheduleChange, type CalendarChange } from "../domain/changes";
import {
  allDayEvents,
  availabilityCells,
  axisTicks,
  clashIds,
  displaySubject,
  findClashes,
  freeMinutes,
  nextUp,
  pastCutoffMin,
  timedSegments,
  workWindow,
  type AvailabilityCell,
  type Clash,
  type DaySegment,
} from "../domain/day";
import { SEARCH_WORKDAYS, nextFree, type Schedule } from "../domain/freeBusy";
import { peopleToCheck, resolveOptions } from "../domain/resolve";
import { addDays, atDayMinutes, dayLabel, dayStart, fd, hm, startOfWeekMonday } from "../domain/time";
import { useCalendarKeys } from "../hooks/useCalendarKeys";
import type { MeetingThread } from "../services/meetingThread";
import type { CalendarToastState } from "../hooks/useChangeQueue";
import { ListIcon, SparkIcon, TimelineIcon, WarnIcon } from "./CalendarIcons";
import { CalendarLoadError } from "./CalendarLoadError";
import { ScheduleList } from "./ScheduleList";
import { Timeline } from "./Timeline";
import styles from "./CalendarModule.module.css";

export type ScheduleMode = "timeline" | "list";

interface DayViewProps {
  readonly day: string;
  readonly today: string;
  readonly now: Date;
  readonly nowMin: number;
  readonly events: readonly CalEvent[];
  readonly workingHours: WorkingHours;
  readonly loading: boolean;
  readonly error: string | null;
  readonly truncated: boolean;
  readonly onRetry: () => void;
  readonly mode: ScheduleMode;
  readonly onModeChange: (mode: ScheduleMode) => void;
  readonly acks: Readonly<Record<string, string>>;
  readonly onLeaveIt: (key: string, signature: string) => void;
  readonly onOpenEvent: (eventId: string) => void;
  /** Start a new meeting, at the free time clicked (null: no time yet). */
  readonly onBook: (start: Date | null) => void;
  /** Start a new meeting on the time finder ("Find a Time"). */
  readonly onFind: () => void;
  /** Whether the token allows changing the calendar (drag, Resolve, Focus Hour). */
  readonly writable: boolean;
  /** Every address of your mailbox (see Self). */
  readonly selfEmails: ReadonlySet<string>;
  /** A meeting just moved or added, to highlight; null when there is none. */
  readonly flashId: string | null;
  /** Show a change now and send it when the Undo window ends. */
  readonly onStage: (change: CalendarChange) => void;
  readonly onNotify: (toast: CalendarToastState) => void;
  readonly onNavigate: (day: string) => void;
  /** Colleagues' free/busy, for "move to the next time everyone is free". */
  readonly loadSchedules: (emails: readonly string[], start: Date, end: Date) => Promise<Map<string, Schedule>>;
  /** Your own meetings between two times, for Resolve's search past the week on screen. */
  readonly loadEvents: (start: Date, end: Date) => Promise<readonly CalEvent[]>;
  /** A meeting's attachments, for naming them in the list. */
  readonly loadAttachments: (eventId: string) => Promise<readonly AttachmentInfo[]>;
  /** A series' repeat label ("Repeats weekly"), for the list. */
  readonly loadRepeat: (seriesMasterId: string) => Promise<string | null>;
  /** Who sent the latest email about a meeting, for the list's "Mail ·" tag. */
  readonly loadThread: (event: CalEvent) => Promise<MeetingThread | null>;
  /** A meeting whose clash to show with its Resolve options open (the meeting screen's See Options). */
  readonly resolveFor: string | null;
  /** `resolveFor` has been acted on. */
  readonly onResolveOpened: () => void;
}

/** Colleagues whose availability couldn't be read count as unknown, never free. */
const unknownFor = (emails: readonly string[]) =>
  new Map<string, Schedule>(emails.map((e) => [e, { status: "unknown", busy: [] }]));

/** Axis label: "08" on the hour, "17:30" otherwise. */
const axisLabel = (min: number) => (min % 60 === 0 ? String(min / 60).padStart(2, "0") : hm(min));

/** "in 20 min" / "in 1h 20min". */
const startsIn = (min: number) => (min >= 60 ? fd(min) : `${min} min`);

/** What an availability cell says it is, for its tooltip. */
function cellLabel(c: AvailabilityCell): string {
  if (c.state === "conf") return "clash";
  if (c.state === "busy") return "busy";
  return c.past ? "past" : "free — click to book";
}

const CELL_CLASS: Record<AvailabilityCell["state"], string> = {
  conf: styles.availConf!,
  busy: styles.availBusy!,
  free: styles.availFree!,
};

/** Axis labels sit inside the strip: the first starts at its tick, the last ends at it, the rest centre on it. */
function tickShift(at: number): number {
  if (at === 0) return 0;
  return at === 1 ? -100 : -50;
}

/** The availability strip's tooltip: your working hours, and whether they're from Outlook. */
function hoursSourceLabel(hours: WorkingHours): string {
  const label = `${hm(hours.startMin)}–${hm(hours.endMin)}`;
  return hours.source === "outlook" ? `Your Outlook working hours: ${label}` : `Default working hours: ${label}`;
}

interface NextUpCardProps {
  readonly seg: DaySegment;
  /** It's on now, rather than next. */
  readonly current: boolean;
  readonly nowMin: number;
  readonly onOpenEvent: (eventId: string) => void;
}

/** Today's meeting on now, or the next one. */
function NextUpCard({ seg, current, nowMin, onOpenEvent }: Readonly<NextUpCardProps>) {
  return (
    <div className={styles.next}>
      <span className={styles.nextBody}>
        <span className={styles.nextKicker}>
          {current ? `Happening now · ends ${hm(seg.endMin)}` : `Next up · in ${startsIn(seg.startMin - nowMin)}`}
        </span>
        <span className={styles.nextTitle}>{displaySubject(seg.event)}</span>
        <span className={styles.nextMeta}>
          {hm(seg.startMin)}–{hm(seg.endMin)}
          {seg.event.location ? ` · ${seg.event.location}` : ""}
        </span>
      </span>
      <button type="button" className={`${styles.btn} ${styles.btnSecondary}`} onClick={() => onOpenEvent(seg.event.id)}>
        Open
      </button>
    </div>
  );
}

/** The day view below the toolbar: next up, availability, clashes and the schedule. */
export function DayView({
  day,
  today,
  now,
  nowMin,
  events,
  workingHours,
  loading,
  error,
  truncated,
  onRetry,
  mode,
  onModeChange,
  acks,
  onLeaveIt,
  onOpenEvent,
  onBook,
  onFind,
  writable,
  selfEmails,
  flashId,
  onStage,
  onNotify,
  onNavigate,
  loadSchedules,
  loadEvents,
  loadAttachments,
  loadRepeat,
  loadThread,
  resolveFor,
  onResolveOpened,
}: Readonly<DayViewProps>) {
  const segs = useMemo(() => timedSegments(events, day), [events, day]);
  const allDay = useMemo(() => allDayEvents(events, day), [events, day]);
  const clashes = useMemo(() => findClashes(segs, acks), [segs, acks]);
  const clashing = useMemo(() => clashIds(clashes), [clashes]);
  // The clash being resolved, and once loaded its attendees' free/busy and your own meetings past the week on
  // screen (`later`), both known up to `knownUntil`.
  const [resolving, setResolving] = useState<{
    key: string;
    loaded: { schedules: Map<string, Schedule>; later: readonly CalEvent[]; knownUntil: Date } | null;
  } | null>(null);
  // The clash the band shows: the first one, unless See Options asked for a particular meeting's.
  const [shownKey, setShownKey] = useState<string | null>(null);

  const startResolve = (clash: Clash) => {
    // The meeting you own moves (the later one if you own both or neither), as in resolveOptions.
    const mover = clash.a.isOrganizer && !clash.b.isOrganizer ? clash.a : clash.b;
    const people = peopleToCheck(mover, selfEmails);
    setResolving({ key: clash.key, loaded: null });
    // Option A searches from this day over the next few working days, which can run past the week on screen.
    const from = dayStart(day);
    const to = dayStart(addDays(day, SEARCH_WORKDAYS * 2));
    const weekEnd = dayStart(addDays(startOfWeekMonday(day), 7));
    void Promise.all([
      people.length === 0
        ? new Map<string, Schedule>()
        : loadSchedules(people, from, to).catch((e: unknown) => {
            logConnectorFailure("calendar", e, "load-resolve-schedules");
            return unknownFor(people);
          }),
      loadEvents(weekEnd, to).then(
        (later) => ({ later, knownUntil: to }),
        // Your calendar past this week couldn't be read: option A looks within the week on screen only.
        (e: unknown) => {
          logConnectorFailure("calendar", e, "load-resolve");
          return { later: [], knownUntil: weekEnd };
        },
      ),
    ]).then(([schedules, own]) =>
      setResolving((r) => (r?.key === clash.key ? { key: r.key, loaded: { schedules, ...own } } : r)),
    );
  };

  // See Options on a meeting: once the day has loaded, show that meeting's clash with Resolve open.
  // (Nothing to show if it was left as is or has since stopped clashing.)
  useEffect(() => {
    if (!resolveFor || loading) return;
    const target = clashes.find((c) => c.a.id === resolveFor || c.b.id === resolveFor);
    if (target) {
      setShownKey(target.key);
      startResolve(target);
    }
    onResolveOpened();
  }, [resolveFor, loading, clashes]);

  // A drop on the timeline: move your own meeting, or propose a time for someone else's.
  const handleMove = (event: CalEvent, startMin: number, endMin: number, how: "move" | "resize") => {
    const start = atDayMinutes(day, startMin);
    const end = atDayMinutes(day, endMin);
    const mode = moveModeFor(event);
    if (mode === "reschedule") onStage(rescheduleChange(event, start, end, how));
    else if (mode === "propose") onStage(proposeChange(event, start, end));
  };

  const protectFocusHour = () => {
    const slot = nextFree({
      events,
      moving: null,
      people: [],
      schedules: new Map(),
      durationMin: 60,
      fromDay: day,
      fromMin: day === today ? nowMin : workingHours.startMin,
      hours: workingHours,
      now,
      sameDayOnly: true,
    });
    if (slot) onStage(focusChange(slot.start, slot.end));
    else onNotify({ kind: "info", message: `No free hour left on ${dayLabel(day)}` });
  };

  // F protects a focus hour, where the Focus Hour button is shown.
  useCalendarKeys((e) => {
    if (e.key.toLowerCase() !== "f" || e.altKey || e.ctrlKey || e.metaKey || e.repeat) return;
    if (loading || error || !writable || day < today) return;
    e.preventDefault();
    protectFocusHour();
  });

  if (error) return <CalendarLoadError message={error} onRetry={onRetry} />;

  if (loading) return <div className={styles.empty}>Loading your calendar…</div>;

  const isToday = day === today;
  const pastMin = pastCutoffMin(day, today, nowMin);
  const workDay = workWindow(workingHours, day);
  // On a non-working day the strip still spans your usual hours.
  const strip = workDay ?? { startMin: workingHours.startMin, endMin: workingHours.endMin };
  const cells = availabilityCells(segs, pastMin, strip);
  const up = isToday ? nextUp(segs, nowMin) : null;
  const clash = clashes.find((c) => c.key === shownKey) ?? clashes[0];
  const hoursSource = hoursSourceLabel(workingHours);
  // Today: an hour before now, on the half hour. Other days: just before the working day starts.
  const scrollToMin = isToday ? Math.floor((nowMin - 60) / 30) * 30 : workingHours.startMin - 30;
  const nowLineMin = isToday ? nowMin : null;
  const timelineHint = writable
    ? "Drag to move · drag the bottom edge to resize · click empty time to book"
    : "Select a meeting to open it · click empty time to book · changes are made in Outlook";

  return (
    <>
      {up && <NextUpCard seg={up.seg} current={up.current} nowMin={nowMin} onOpenEvent={onOpenEvent} />}

      <div className={styles.sec}>
        <div className={styles.secH}>
          <span className={styles.secBar} />
          <span className={styles.secTitle}>Availability</span>
          <span className={styles.secMeta}>
            {hm(strip.startMin)}–{hm(strip.endMin)}
          </span>
          <span className={`${styles.secRight} ${styles.secMeta}`} title={hoursSource}>
            {workDay ? `${fd(freeMinutes(segs, workDay))} free` : "Non-working day"}
          </span>
        </div>
        <div className={styles.avail}>
          {cells.map((c) => {
            const label = `${hm(c.startMin)} · ${cellLabel(c)}`;
            const cls = `${styles.availCell} ${CELL_CLASS[c.state]} ${c.past ? styles.availPast : ""}`;
            return c.state === "free" && !c.past ? (
              <button
                key={c.startMin}
                type="button"
                className={cls}
                title={label}
                aria-label={label}
                onClick={() => onBook(atDayMinutes(day, c.startMin))}
              />
            ) : (
              <span key={c.startMin} className={cls} title={label} />
            );
          })}
        </div>
        <div className={styles.availAxis} aria-hidden="true">
          {axisTicks(strip).map((t) => (
            <span
              key={t.min}
              style={{ left: `${t.at * 100}%`, transform: `translateX(${tickShift(t.at)}%)` }}
            >
              {axisLabel(t.min)}
            </span>
          ))}
        </div>
      </div>

      {clash && (
        <div className={`${styles.band} ${styles.bandErr}`}>
          <div className={styles.bandTitle}>
            <WarnIcon />
            Needs a Decision
            {clashes.length > 1 && clash && (
              <span className={styles.bandCount}>
                {clashes.indexOf(clash) + 1} of {clashes.length}
              </span>
            )}
          </div>
          <div className={styles.bandText}>
            <b>
              {hm(clash.startMin)}–{hm(clash.endMin)} clashes.
            </b>{" "}
            {displaySubject(clash.a)} overlaps {displaySubject(clash.b)} by {fd(clash.endMin - clash.startMin)}
          </div>
          {resolving?.key !== clash.key ? (
            <div className={styles.bandActions}>
              {writable && (
                <button type="button" className={styles.btn} onClick={() => startResolve(clash)}>
                  Resolve
                </button>
              )}
              <button
                type="button"
                className={`${styles.btn} ${styles.btnSecondary}`}
                onClick={() => onLeaveIt(clash.key, clash.signature)}
              >
                Leave It
              </button>
            </div>
          ) : (
            <div className={styles.resolveList}>
              {resolving.loaded === null ? (
                <span className={styles.hint}>Checking availability…</span>
              ) : (
                (() => {
                  const options = resolveOptions(clash, {
                    events: [...events, ...resolving.loaded.later],
                    knownUntil: resolving.loaded.knownUntil,
                    schedules: resolving.loaded.schedules,
                    hours: workingHours,
                    now,
                    selfEmails,
                  });
                  if (options.length === 0) {
                    return (
                      <span className={styles.hint}>
                        AiVA couldn't find a change that clears this clash. Open the meetings to sort it out in Outlook.
                      </span>
                    );
                  }
                  return options.map((o) => (
                    <button
                      key={o.key}
                      type="button"
                      className={styles.opt}
                      onClick={() => {
                        setResolving(null);
                        onStage(o.change);
                        if (o.navigateTo) onNavigate(o.navigateTo);
                      }}
                    >
                      <span className={styles.optKey}>{o.key}</span>
                      <span className={styles.optBody}>
                        <span className={styles.optName}>{o.title}</span>
                        <span className={styles.optDesc}>{o.detail}</span>
                      </span>
                    </button>
                  ));
                })()
              )}
              <button type="button" className={`${styles.newLink} ${styles.alignStart}`} onClick={() => setResolving(null)}>
                Not now
              </button>
            </div>
          )}
        </div>
      )}

      {/* In timeline mode the section takes the rest of the pane, so the timeline box ends at its bottom edge. */}
      <div className={`${styles.sec} ${mode === "timeline" ? styles.secFill : ""}`}>
        <div className={styles.secH}>
          <span className={styles.secBar} />
          <span className={styles.secTitle}>Schedule</span>
          <span className={styles.secMeta}>{segs.length + allDay.length}</span>
          <span className={styles.secRight}>
            {writable && day >= today && (
              <button type="button" className={styles.newLink} onClick={protectFocusHour} title="Protect a focus hour">
                <SparkIcon />
                Focus Hour
              </button>
            )}
            <span className={`${styles.seg} ${styles.segGap}`}>
              <button
                type="button"
                className={mode === "timeline" ? styles.segOn : ""}
                aria-pressed={mode === "timeline"}
                aria-label="Timeline"
                title="Timeline"
                onClick={() => onModeChange("timeline")}
              >
                <TimelineIcon />
              </button>
              <button
                type="button"
                className={mode === "list" ? styles.segOn : ""}
                aria-pressed={mode === "list"}
                aria-label="List"
                title="List"
                onClick={() => onModeChange("list")}
              >
                <ListIcon />
              </button>
            </span>
          </span>
        </div>

        {mode === "timeline" && allDay.length > 0 && (
          <div className={styles.allDay}>
            <span className={styles.allDayLabel}>All day</span>
            {allDay.map((e) => (
              <button key={e.id} type="button" className={styles.allDayChip} onClick={() => onOpenEvent(e.id)}>
                {displaySubject(e)}
              </button>
            ))}
          </div>
        )}

        {mode === "timeline" ? (
          <>
            <Timeline
              day={day}
              scrollToMin={scrollToMin}
              segs={segs}
              clashing={clashing}
              flashId={flashId}
              pastMin={pastMin}
              nowMin={nowLineMin}
              writable={writable}
              onOpenEvent={onOpenEvent}
              onBook={(startMin) => onBook(atDayMinutes(day, startMin))}
              onMove={handleMove}
            />
            <span className={styles.hint}>{timelineHint}</span>
          </>
        ) : (
          <ScheduleList
            segs={segs}
            allDay={allDay}
            clashing={clashing}
            flashId={flashId}
            workStartMin={workingHours.startMin}
            onOpenEvent={onOpenEvent}
            onBook={(startMin) => onBook(startMin === null ? null : atDayMinutes(day, startMin))}
            onFind={onFind}
            loadAttachments={loadAttachments}
            loadRepeat={loadRepeat}
            loadThread={loadThread}
          />
        )}

        {truncated && <span className={styles.hint}>Showing the first meetings only — this day has more than AiVA loads.</span>}
      </div>
    </>
  );
}
